import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import styled from 'styled-components/macro';
import { useFormik } from 'formik';
import DebitDetailsForm from './DebitDetailsForm';
import DonorPersonalDetailsForm from './DonorPersonalDetailsForm';
import { DebitDetailsWithoutDebitSchema, DonorCreateSchemas } from '../../donor-schemas';
import { ApiCreateDonor } from '../../../../project-types/donor/api-types';
import { DonorRegistrationError } from '../../../../feature/donorRegistration';

interface CreateDonorFormsProps {
  onDonorDetailsBackClick: () => void;
  onFormConfirm: (formData: ApiCreateDonor['data']) => Promise<void>;
  inviteCode: string;
  onLoginClickHandler: () => void;
}

export default function CreateDonorForms({
  onDonorDetailsBackClick,
  inviteCode,
  onFormConfirm,
  onLoginClickHandler,
}: CreateDonorFormsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDirectDebit, setIsDirectDebit] = useState(true);
  const [registrationError, setRegistrationError] = useState<DonorRegistrationError | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (registrationError) errorRef.current?.focus();
  }, [registrationError]);

  // const onSelectAddressHandler = (address: FindAddressApiResponseAddressItem) => {
  //   formik.setFieldValue('address1', address.line_1);
  //   formik.setFieldValue('address2', address.line_2);
  //   formik.setFieldValue('country', address.country);
  //   formik.setFieldValue('town', address.town_or_city);
  // };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      isCorporation: false,
      email: '',
      password: '',
      confirmPassword: '',
      title: '',
      postcode: '',
      address1: '',
      address2: '',
      address3: '',
      town: '',
      county: '',
      country: '',
      phoneNumber: '',
      bankAccountName: '',
      branchSortCode1: '',
      branchSortCode2: '',
      branchSortCode3: '',
      accountNumber: '',
      bankUnhookException: false,
      agree: false,
    },
    validationSchema: isDirectDebit
      ? DonorCreateSchemas[currentPage - 1]
      : DebitDetailsWithoutDebitSchema,
    onSubmit: async (values) => {
      setRegistrationError(null);
      const {
        email, password, agree, confirmPassword, ...formValues
      } = values;
      const createDonorData = {
        email,
        password,
        captcha: '123456',
        donorProfile: { ...formValues },
      };

      try {
        await onFormConfirm(createDonorData);
      } catch (error) {
        const failure = error instanceof DonorRegistrationError
          ? error
          : new DonorRegistrationError('We could not complete your registration. Please try again.');
        if (failure.credentialsRejected) setCurrentPage(1);
        setRegistrationError(failure);
      }
    },
  });

  const renderForm = (page: number) => {
    switch (page) {
      case 1:
        return (
          <DonorPersonalDetailsForm
            onLoginClickHandler={onLoginClickHandler}
            onBackDetailsClick={onDonorDetailsBackClick}
            onButtonClickHandler={setCurrentPage}
            formikInstance={formik}
          />
        );
      case 2:
        return (
          <DebitDetailsForm
            onButtonClickHandler={setCurrentPage}
            formikInstance={formik}
            isDirectDebit={isDirectDebit}
            setIsDirectDebit={setIsDirectDebit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormStyled onSubmit={formik.handleSubmit}>
      {registrationError && (
        <div ref={errorRef} tabIndex={-1} style={{ marginBottom: 24 }}>
          <Alert
            type="error"
            showIcon
            message="Registration could not be completed"
            description={registrationError.message}
            action={registrationError.credentialsRejected && (
              <Button htmlType="button" onClick={onLoginClickHandler}>Sign in</Button>
            )}
          />
        </div>
      )}
      {renderForm(currentPage)}
    </FormStyled>
  );
}

const FormStyled = styled.form`
  width: 100%;
`;
